/**
 * Header behaviour — ported from the .in engine's @common/headers/header.js.
 *
 * The class names here are NOT free choices: the migrated stylesheet drives the
 * whole mobile menu off them.
 *
 *   #gBody.open   .g-nav-menu slides in           (header.scss:734)
 *   #gBody.close  plays the closing animation     (header.scss:747)
 *   #overlay      the dimmer, shown by the same :is(#gBody.open, …) rule
 *
 * The earlier version of this file invented `.g-nav-menu[data-open]` and
 * `body.nav-open`, which no rule in the styleset matches — so the burger fired
 * and nothing moved.
 *
 * Dropped from the .in original, because the markup for them does not exist
 * here: the `#gNavLeft` drill-down submenus (that id belongs to an older nav
 * structure — .in's own header.ejs no longer emits it either) and the app-link
 * GA hooks.
 */
import gaSC, { npga2 } from "@common/utils/ga";

/** Matches the 0.85s elementClosed animation in header.scss. */
const CLOSE_ANIMATION_MS = 800;

export function headerInit() {
  const body = document.getElementById("gBody") || document.body;

  bannerPresent(body);
  mobileMenu(body);
  countrySelector(body);
  footerGaEvents();
}

/**
 * The country flag dropdown, desktop and mobile.
 *
 * The stylesheet shows the list off `[data-dropdown="active"]` on the button,
 * so the only job here is flipping that attribute — same contract as .in's
 * jQuery version, minus jQuery.
 *
 * The mobile button's guard matters: its <ul> is a child of the <button>, so a
 * tap on a flag bubbles back up and would toggle the dropdown shut before the
 * link navigates.
 */
function countrySelector(body) {
  const buttons = ["countryflag", "countryflagmobile"]
    .map((id) => document.getElementById(id))
    .filter(Boolean);
  if (!buttons.length) return;

  const close = (button) => button.setAttribute("data-dropdown", "inactive");

  buttons.forEach((button) => {
    button.addEventListener("click", (event) => {
      if (event.target.closest(".flagdropdownlist")) return;
      const isOpen = button.getAttribute("data-dropdown") === "active";
      // Only one dropdown can be open, and the other one is the same control at
      // a different breakpoint.
      buttons.forEach(close);
      if (!isOpen) button.setAttribute("data-dropdown", "active");
    });
  });

  // Anywhere else on the page closes it.
  body.addEventListener("click", (event) => {
    buttons.forEach((button) => {
      if (!button.contains(event.target)) close(button);
    });
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") buttons.forEach(close);
  });
}

/**
 * The ribbon shifts the sticky header down. The generator already sets this
 * class server-side from the admin config, so this only keeps the DOM honest
 * if the ribbon is ever removed at runtime.
 */
function bannerPresent(body) {
  const hasBanner = !!document.querySelector(".top-header-banner");
  body.classList.toggle("banner-present", hasBanner);

  const tab = document.getElementById("gmTab");
  if (tab) tab.classList.toggle("banner-tab-present", hasBanner);
}

function mobileMenu(body) {
  const burger = document.getElementById("gBurger");
  if (!burger) return;

  let opening = false;

  const closeMenu = () => {
    body.classList.add("close");
    body.classList.remove("open");
    detachDismissers();

    // Leave `close` on for the length of the animation, then clear it — the
    // stylesheet keys its exit animation off that class.
    setTimeout(() => body.classList.remove("close"), CLOSE_ANIMATION_MS);
  };

  // Tapping the dimmer or the header strip closes the menu, same as .in.
  const dismissers = () =>
    [
      document.getElementById("overlay"),
      document.querySelector("header > .g-top"),
    ].filter(Boolean);

  const onDismiss = () => closeMenu();
  const detachDismissers = () =>
    dismissers().forEach((node) =>
      node.removeEventListener("click", onDismiss),
    );

  burger.addEventListener("click", () => {
    // The dismissers are attached a beat late (below); without this guard the
    // click that opened the menu would immediately close it again.
    if (opening) return;
    opening = true;

    body.classList.add("open");

    setTimeout(() => {
      detachDismissers();
      dismissers().forEach((node) =>
        node.addEventListener("click", onDismiss, { once: true }),
      );
      opening = false;
    }, 200);
  });

  const closeButton = document.getElementById("close");
  if (closeButton) closeButton.addEventListener("click", closeMenu);
}

function footerGaEvents() {
  document.querySelectorAll(".footer-list li a").forEach((link) => {
    link.addEventListener("click", () =>
      gaSC("Home Page Footer", link.textContent.trim()),
    );
  });
  // .in's 'Nav Stores' / 'Nav Categories' fire when its mobile drill-down
  // submenus open. This header has no drill-down — the nav items are plain
  // links — so the closest equivalent is the click on the item itself. The
  // label mirrors .in's: the item's own text.
  document
    .querySelectorAll(".g-nav-main.store a, .g-nav-main.store")
    .forEach((el) => {
      el.addEventListener("click", () =>
        gaSC("Nav Stores", el.textContent.trim()),
      );
    });
  document
    .querySelectorAll(".g-nav-main.other a, .g-nav-main.other")
    .forEach((el) => {
      el.addEventListener("click", () =>
        gaSC("Nav Categories", el.textContent.trim()),
      );
    });
  npga2(".idu", "Indulge");
}
